import { useCallback } from 'react';
import { getSupplierReturnReceiptColumns } from '../cols-definition';
import { SearchFormValues } from '../schema';
import { useDynamicSearch } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

interface UseSearchTableProps {
  searchData: (filters: BanHangSearchParams, page?: number, searchPageSize?: number) => Promise<void>;
  searchParams: SearchFormValues;
  setSearchParams: (params: SearchFormValues) => void;
  loading: boolean;
  handleViewClick: (obj: BanHangRecord) => void;
  sumTotal: number | null;
  isFiltered: boolean;
  handlePageChange: (page: number) => Promise<void>;
}

export const useSearchTable = ({
  searchData,
  searchParams,
  setSearchParams,
  loading,
  handleViewClick,
  sumTotal,
  isFiltered,
  handlePageChange
}: UseSearchTableProps) => {
  const onSearch = useCallback(
    async (params: BanHangTableParams) => {
      await searchData(params);
    },
    [searchData]
  );

  const onInitialSearch = useCallback(
    async (formData: BanHangPayload, onSearchFn: (params: BanHangTableParams) => Promise<void>) => {
      const { dynamic_filters, ...searchParamsOnly } = formData;
      setSearchParams(searchParamsOnly as SearchFormValues);
      await onSearchFn(formData);
    },
    [setSearchParams]
  );

  const getColumns = useCallback(() => {
    const cols = getSupplierReturnReceiptColumns(handleViewClick, sumTotal, loading);
    return cols.map(col => ({
      field: col.field,
      type: col.type
    }));
  }, [handleViewClick, sumTotal, loading]);

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch,
    params: searchParams,
    onInitialSearch,
    getColumns,
    endpoint: `${QUERY_KEYS.PHIEU_NHAP_HANG_BAN_TRA_LAI}/filter`,
    pageSize: 20,
    sumField: 't_tt_nt'
  });

  const onPageChange = useCallback(
    async (page: number) => {
      if (hasFilterParams) {
        await handleSearchPageChange(page);
      } else if (isFiltered) {
        await searchData(searchParams, page);
      } else {
        await handlePageChange(page);
      }
    },
    [hasFilterParams, handleSearchPageChange, isFiltered, searchParams, searchData, handlePageChange]
  );

  return {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering: isSearching
  };
};
